import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { map, Observable } from "rxjs";
import { environment } from "../../../environments/environment";
import { AuthService, Default_SessionUserDetails, SessionData_Response } from "../../core/services/auth.service";
import {
  dateFromServerString,
  fileTooltipFromServerLinkTag,
  getServerLinkTagContent,
  normalizeServerString,
  stringDateToServerString,
} from "../../core/utils/convertors";
import { CaseSearchEntry, PeopleSearchFormData, PersonSearchCase, PersonSearchEntry } from "./party-search.model";

@Injectable({
  providedIn: "root",
})
export class PartySearchApiService {

  //#region Private State
  private currentUser: SessionData_Response = Default_SessionUserDetails; //Stores the currently logged-in user session details.
  //#endregion

  //#region Constructor
  constructor(
    private http: HttpClient,
    private auth: AuthService,
  ) {
    // Subscribe to session updates to track the current user
    this.auth.currentSession$.subscribe((res) => {
      this.currentUser = res;
    });
  }
  //#endregion

  //#region Public Methods

  /**
   * Validates or processes a case file number via backend service.
   * @param caseNo 
   * @returns 
   */
  public handleFileNumberSearch(caseNo: string): Observable<string> {
    const path = "/WebServices/CaseInfo.asmx/handleFileNumberSearch";

    return this.http
      .post<string>(environment.api.url + path, {
        caseNo: caseNo,
      }, { params: { 'showLoader': true } })
      .pipe(map((wrapper) => wrapper));
  }

  /**
   * Searches for people based on provided search criteria
   * @param orderBy 
   * @param pageNumber 
   * @param sortDir 
   * @param searchParams 
   * @param iSearch 
   * @returns 
   */
  public searchPeople(
    orderBy: string = "Name",
    pageNumber?: number | null,
    sortDir?: "desc" | "asc" | null,
    searchParams?: PeopleSearchFormData,
    iSearch?: string | null
  ): Observable<PersonSearchEntry[]> {
    const path =
      "WebServices/PartySearchResult.asmx/GetPersonSearchResultCaseSearch";

    return this.http
      .post<string>(
        environment.api.url + path,
        {
          pageNumber: pageNumber || 1,
          orderby: orderBy || "",
          sortDir: sortDir || "asc",
          iSearch: iSearch || "",
          lastName: searchParams?.lastName || "",
          firstName: searchParams?.firstName || "",
          middleName: searchParams?.middleName || "",
          offenceFrom:
            stringDateToServerString(searchParams?.offenceFrom) || "",
          offenceTo: stringDateToServerString(searchParams?.offenceTo) || "",
          leAgency: searchParams?.agency || "",
          oca: searchParams?.reportNumber || "",
          courtNo: searchParams?.courtCaseNumber || "",
          localId: searchParams?.localId || "",
          stateId: searchParams?.stateId || "",
          ocn: searchParams?.fingerprint || "",
          dateOfBirth:
            stringDateToServerString(searchParams?.dateOfBirth) || "",
          defendantNumber: searchParams?.defendantNumber || "",
        },
        { params: { showLoader: true } }
      )
      .pipe(
        map((wrapper) =>
          (<unknown[][]>JSON.parse(wrapper)).map(
            (parsed) =>
              <PersonSearchEntry>{
                defendantNumber: parsed[0] as number,
                name: normalizeServerString(parsed[1] as string),
                localId: normalizeServerString(parsed[2] as string),
                shortAddress: normalizeServerString(parsed[3] as string),
                status: normalizeServerString(parsed[4] as string),
                dlNumber: normalizeServerString(parsed[5] as string),
                race: normalizeServerString(parsed[6] as string),
                sex: normalizeServerString(parsed[7] as string),
                dateOfBirth: dateFromServerString(parsed[8] as string),
                fullAddress: normalizeServerString(parsed[10] as string),
                phoneNumber: normalizeServerString(parsed[12] as string),
              }
          )
        )
      );
  }

  /**
   * Retrieves case details for a given defendant.
   * @param defendantNumber 
   * @param caseStatus 
   * @param reportNumber 
   * @returns 
   */
  public searchCaseByPerson(
    defendantNumber: string,
    caseStatus?: string | null,
    reportNumber?: string | null,
  ): Observable<CaseSearchEntry[]> {
    const path = '/WebServices/PartySearchResult.asmx/PersonSearchCaseSearch';

    return this.http
      .post<string>(environment.api.url + path, {
        defendantNumberList: defendantNumber,
        userId: this.currentUser.UserId || '',
        caseStatus: caseStatus || '',
        reportNumber: reportNumber || '',
      })
      .pipe(
        map(wrapper =>
          (<unknown[][]>JSON.parse(wrapper)).map(
            arr =>
              <CaseSearchEntry>{
                caseId: normalizeServerString(arr[10] as string),
                defendantNumber: arr[11] as number,
                jurisdiction: normalizeServerString(arr[0] as string),
                causeNumber: normalizeServerString(arr[2] as string),
                fileNumber: getServerLinkTagContent(arr[1] as string),
                fileNumberTooltip: fileTooltipFromServerLinkTag(arr[1] as string),
                reportNumber: normalizeServerString(arr[3] as string),
                reportNumberTooltip: normalizeServerString(arr[13] as string),
                charge: normalizeServerString(arr[4] as string),
                chargeTooltip: normalizeServerString(arr[12] as string),
                caseType: normalizeServerString(arr[5] as string),
                issuedDate: dateFromServerString(arr[6] as string),
                status: normalizeServerString(arr[7] as string),
                stage: normalizeServerString(arr[8] as string),
                finalDispDate: dateFromServerString(arr[9] as string),
                isCivilCase: normalizeServerString(arr[14] as string),
              }
          )
        )
      );
  }

  public getPersonSearchCases(params: {
    reportNumber: string;
    courtNumber: string;
    ocn: string;
  }): Observable<PersonSearchCase[]> {
    const path = 'WebServices/PartySearchResult.asmx/MultiPleFileResultCaseSearch';

    return this.http
      .post<string>(environment.api.url + path, {
        ReportNumber: params.reportNumber,
        CourtNumber: params.courtNumber,
        OCN: params.ocn,
      })
      .pipe(
        map(wrapper =>
          (<unknown[][]>JSON.parse(wrapper)).map(parsed => {
            return <PersonSearchCase>{
              defendantFullName: normalizeServerString(parsed[0] as string),
              jurisdiction: normalizeServerString(parsed[1] as string),
              offenseFromDate: normalizeServerString(parsed[2] as string),
              reportNumber: normalizeServerString(parsed[3] as string),
              fileNumber: getServerLinkTagContent(normalizeServerString(parsed[4] as string)),
              fileNumberTooltip: fileTooltipFromServerLinkTag(
                normalizeServerString(parsed[4] as string)
              ),
              causeNumber: normalizeServerString(parsed[5] as string),
              chargeDescription: normalizeServerString(parsed[6] as string),
              severity: normalizeServerString(parsed[7] as string),
              status: normalizeServerString(parsed[8] as string),
              stage: normalizeServerString(parsed[9] as string),
              ticketNumber: normalizeServerString(parsed[10] as string),
              license: normalizeServerString(parsed[11] as string),
              caseReportingType: normalizeServerString(parsed[12] as string),
              oldFile: normalizeServerString(parsed[13] as string),
              agencyTooltip: normalizeServerString(parsed[14] as string),
              caseId: normalizeServerString(parsed[15] as string),
              defendantNumber: parsed[16] as number,
              chargeDescriptionTooltip: normalizeServerString(parsed[17] as string),
              ageTooltip: normalizeServerString(parsed[18] as string),
              lastEventTooltip: normalizeServerString(parsed[19] as string),
              assign: normalizeServerString(parsed[21] as string),
              isCivilCase: normalizeServerString(parsed[22] as string),
              rawValue: normalizeServerString(parsed.flat().join(' ')),
            };
          })
        )
      );
  }
  //#endregion
}
